/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { MeteoWeatherCodeHeader } from './MeteoWeatherCodeHeader.component';
import { MeteoHomeContext } from 'pages/meteo/[type]/[locality]';
import { Geopoint } from 'pleinchamp-api-client';
import React, { MutableRefObject, useContext } from 'react';
import { IconButton } from '@components/buttons/IconButton/IconButton.component';
import { PlcTextListLoader } from '@components/Loaders/PlcTextLoader.component';
import StickyHeader from '@components/StickyHeader/StickyHeader.component';
import { Text } from '@components/Text/Text.component';
import { TextProps } from '@components/Text/Text.types';
import { MeteoChevronLeftIcon } from '@icons/MeteoChevronLeft.icon';
import { MeteoChevronRightIcon } from '@icons/MeteoChevronRight.icon';
import { MeteoHomeTabs } from '@meteo/business/Meteo.types';
import styles from '@meteo/components/MeteoWeatherTable/MeteoWeatherTableContent/MeteoWeatherTableContent.module.scss';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { PlcColor } from '@styles/colors';
import { usePlcRouter } from '@utils/customHooks';
import { formatDateShortDayAndDayNumber, formatDateToHour } from '@utils/date';
import { buildTargetUrlWithUpdatedParam } from '@utils/url';
import { Dayjs } from 'dayjs';
import { Direction, getScroll } from '../utils';
import { buildDayLabel } from './Header.utils';

interface Props {
  dates: Dayjs[];
  weatherCodes: Array<number | undefined>;
  hoveredIndex?: number;
  format15Days?: boolean;
  formatHourByHour: boolean;
  setHoveredIndex: (index: number | undefined) => void;
  withDays?: boolean;
  handleNavigation: (dir: Direction) => void;
  isLoading?: boolean;
  bottomBoundary?: number;
  top?: number;
  overFlowingContainerRef: MutableRefObject<HTMLDivElement | null>;
  coordinates: Geopoint;
}

const TEXT_PROPS: TextProps[] = [{ variant: 'h5', width: 25 }];
const EMPTY: null[] = [];

function HeaderContent({
  dates,
  weatherCodes,
  hoveredIndex,
  setHoveredIndex,
  withDays,
  handleNavigation,
  format15Days,
  formatHourByHour,
  isLoading,
  overFlowingContainerRef,
  coordinates,
}: Omit<Props, 'bottomBoundary'>) {
  const displayDays = withDays && !isLoading;
  const days = displayDays ? buildDayLabel(dates) : EMPTY;

  const scrollLeft = getScroll(overFlowingContainerRef, Direction.Left);
  const scrollRight = getScroll(overFlowingContainerRef, Direction.Right);

  const isSmartphone = useIsBreakpointDown(Breakpoint.m);
  const {
    day: { setData: setDay },
    defaultTab: { setData: setActiveTab },
  } = useContext(MeteoHomeContext);
  const router = usePlcRouter();
  const { pathname, query } = router;

  function handleHeaderClick(activeDay: Dayjs) {
    if (setActiveTab) {
      setActiveTab(MeteoHomeTabs.HourByHour);
    }
    router.replace(pathname, buildTargetUrlWithUpdatedParam(pathname, 'type', MeteoHomeTabs.HourByHour, query), {
      shallow: true,
    });
    if (setDay) setDay(activeDay.toString());
  }

  return (
    <>
      <tr className={styles.empty}>
        <th className={`${styles.tableNavigation} ${styles.left} ${styles.sides}`} />
        {dates.map((_, i) => (
          <th key={i} className={hoveredIndex === i ? styles.emphasis : ''} onMouseEnter={() => setHoveredIndex(i)} />
        ))}
        <th className={`${styles.tableNavigation} ${styles.right} ${styles.sides}`} />
      </tr>
      {displayDays && (
        <tr className={styles.days}>
          <th className={`${styles.tableNavigation} ${styles.left} ${styles.sides}`} />
          {days.map((day, i) => (
            <th key={i} className={hoveredIndex === i ? styles.emphasis : ''} onMouseEnter={() => setHoveredIndex(i)}>
              {day && <Text flavour="grey" i18nKey={day} tag="span" variant="tiny-title" />}
            </th>
          ))}
          <th className={`${styles.tableNavigation} ${styles.right} ${styles.sides}`} />
        </tr>
      )}
      <tr className={styles.dates}>
        <th className={`${styles.tableNavigation} ${styles.left} ${styles.sides}`}>
          <IconButton onClick={() => handleNavigation(Direction.Left)}>
            <MeteoChevronLeftIcon fillColor={scrollLeft ? PlcColor.GREEN : undefined} width={8} />
          </IconButton>
        </th>
        {dates.map((d, i) => (
          <th
            key={i}
            className={`${formatHourByHour ? '' : 'cursor-pointer'} ${hoveredIndex === i ? styles.emphasis : ''}`}
            onClick={() => (formatHourByHour || isLoading ? null : handleHeaderClick(dates[i]))}
            onMouseEnter={() => setHoveredIndex(i)}
          >
            <div>
              {isLoading ? (
                <PlcTextListLoader fixedWidth textProps={TEXT_PROPS} uniqueKey={`WeatherTableHeaderDate-${i}`} />
              ) : (
                <Text
                  className={format15Days ? styles.tableNavigationDate15Days : ''}
                  tag="span"
                  variant={isSmartphone ? 'h5' : 'h6'}
                >
                  {format15Days ? formatDateShortDayAndDayNumber(d) : formatDateToHour(d)}
                </Text>
              )}
            </div>
          </th>
        ))}
        <th className={`${styles.tableNavigation} ${styles.right} ${styles.sides}`}>
          <IconButton onClick={() => handleNavigation(Direction.Right)}>
            <MeteoChevronRightIcon fillColor={scrollRight ? PlcColor.GREEN : undefined} width={8} />
          </IconButton>
        </th>
      </tr>
      <tr className={styles.weatherCode}>
        <th className={`${styles.tableNavigation} ${styles.left} ${styles.sides}`} />
        {weatherCodes.map((wc, i) => (
          <MeteoWeatherCodeHeader
            key={i}
            code={wc}
            coordinates={coordinates}
            date={dates[i]}
            forceDay={Boolean(format15Days)}
            formatHourByHour={formatHourByHour}
            isEmphasis={i === hoveredIndex}
            isLoading={isLoading}
            onMouseOver={() => setHoveredIndex(i)}
          />
        ))}
        <th className={`${styles.tableNavigation} ${styles.right} ${styles.sides}`} />
      </tr>
    </>
  );
}

function MeteoWeatherTableHeader({
  top,
  overFlowingContainerRef,
  format15Days,
  formatHourByHour,
  dates,
  ...props
}: Props) {
  function handleMouseLeave() {
    props.setHoveredIndex(undefined);
  }

  const isSmartphoneOrTablet = useIsBreakpointDown(Breakpoint.l);

  return (
    <StickyHeader
      containerClass=".overflowing-container"
      overflow={true}
      topComponentClass={isSmartphoneOrTablet ? '.plc-header' : '.plc-thematic-header'}
    >
      <table onMouseLeave={handleMouseLeave}>
        <thead>
          <HeaderContent
            dates={dates}
            format15Days={format15Days}
            formatHourByHour={formatHourByHour}
            overFlowingContainerRef={overFlowingContainerRef}
            {...props}
          />
        </thead>
      </table>
    </StickyHeader>
  );
}

export { MeteoWeatherTableHeader };
